import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getUserReports, getReportTypeLabel, getStatusLabel } from '../../api/reportAPI';
import './MyReports.css';

const MyReports = () => {
  // Now getting user object instead of just token
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedReport, setExpandedReport] = useState(null);

  // Fetch user reports
  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        // Get token from localStorage for API call
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication error. Please log in again.');
        }
        
        const response = await getUserReports(token, page);
        setReports(response.data.data);
        setTotalPages(response.data.pagination.pages);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load reports');
        setLoading(false);
      }
    };

    fetchReports();
  }, [page]); // Removed token dependency, using localStorage instead

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Toggle expanded report
  const toggleExpand = (reportId) => {
    if (expandedReport === reportId) {
      setExpandedReport(null);
    } else {
      setExpandedReport(reportId);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      new: '#6c5ce7',
      under_review: '#fdcb6e',
      in_progress: '#0984e3',
      fixed: '#00b894',
      wont_fix: '#d63031',
      duplicate: '#636e72',
      closed: '#2d3436'
    };
    return colors[status] || '#6c5ce7';
  };

  if (loading && reports.length === 0) {
    return (
      <div className="loading-reports">
        <div className="loading-spinner"></div>
        <p>Loading your reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-error">
        <i className="fa-solid fa-circle-exclamation"></i>
        <h3>Error Loading Reports</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="no-reports">
        <i className="fa-solid fa-clipboard-check"></i>
        <h3>No Reports Found</h3>
        <p>You haven't submitted any reports yet. Use the "Submit a Report" section to report any issues or suggest improvements.</p>
      </div>
    );
  }

  return (
    <div className="my-reports-container">
      <h2>My Reports</h2>
      <p className="reports-description">
        Track the status of your submitted reports and view responses from our team.
      </p>

      <div className="reports-list">
        {reports.map(report => (
          <div 
            key={report._id} 
            className={`report-card ${expandedReport === report._id ? 'expanded' : ''}`}
          >
            <div className="report-header" onClick={() => toggleExpand(report._id)}>
              <div className="report-title-section">
                <h3 className="report-title">{report.title}</h3>
                <div className="report-meta">
                  <span className="report-type">{getReportTypeLabel(report.reportType)}</span>
                  <span className="report-date">{formatDate(report.createdAt)}</span>
                </div>
              </div>
              <div className="report-status-section">
                <span 
                  className="report-status"
                  style={{ backgroundColor: getStatusColor(report.status) }}
                >
                  {getStatusLabel(report.status)}
                </span>
                <button className="expand-btn">
                  <i className={`fa-solid ${expandedReport === report._id ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                </button>
              </div>
            </div>
            
            {expandedReport === report._id && (
              <div className="report-details">
                <div className="detail-section">
                  <h4>Description</h4>
                  <p>{report.description}</p>
                </div>
                
                {report.stepsToReproduce && (
                  <div className="detail-section">
                    <h4>Steps to Reproduce</h4>
                    <p>{report.stepsToReproduce}</p>
                  </div>
                )}
                
                {report.correctInformation && (
                  <div className="detail-section">
                    <h4>Correct Information</h4>
                    <p>{report.correctInformation}</p>
                  </div>
                )}
                
                {report.adminNotes && (
                  <div className="detail-section admin-notes">
                    <h4>Response from Staff</h4>
                    <p>{report.adminNotes}</p>
                  </div>
                )}
                
                {report.attachments && report.attachments.length > 0 && (
                  <div className="detail-section">
                    <h4>Attachments</h4>
                    <div className="attachments-gallery">
                      {report.attachments.map((url, index) => (
                        <a 
                          key={index} 
                          href={url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="attachment-link"
                        >
                          {url.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                            <img src={url} alt={`Attachment ${index + 1}`} />
                          ) : (
                            <div className="file-attachment">
                              <i className="fa-solid fa-file"></i>
                              <span>Attachment {index + 1}</span>
                            </div>
                          )}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="report-id-section">
                  <span>Report ID: {report._id}</span>
                  <span>Priority: {report.priority}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn prev"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            <i className="fa-solid fa-chevron-left"></i> Previous
          </button>
          
          <div className="page-indicator">
            Page {page} of {totalPages}
          </div>
          
          <button
            className="page-btn next"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default MyReports;